import type { ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { ApiProvider, AuthProvider, HandoffGate, SsoCallbackGate, endReasonOf, sessionController, signInUrl } from "@ncp/api-client";
import { UiProvider } from "@ncp/ui";
import { useLocale } from "@ncp/i18n";
import { ECONIC_SESSION_CHANNEL, SessionManager } from "../econic/session";

export const AUTH_STORAGE_KEY = "ncp.assistant.session";
export const API_BASE = import.meta.env.VITE_API_URL ?? "";
const LOGIN_PATH = "/login";

/**
 * The ECONIC session (security §3.3, shell-ia §11): the access token in storage, the rotating refresh cookie, activity shared by every
 * tab, the 30-minute idle and 10-hour absolute limits, and the `econic-session` channel between tabs.
 */
const session = sessionController(AUTH_STORAGE_KEY, { apiBase: API_BASE, channelName: ECONIC_SESSION_CHANNEL });
const getToken = () => session.getToken();
const getFreshToken = () => session.freshToken();
const refresh = async () => ["refreshed", "adopted"].includes(await session.refresh());

function ApiBridge({ children }: { children: ReactNode }) {
  const { locale } = useLocale();
  return (
    <ApiProvider
      baseUrl={API_BASE}
      getToken={getToken}
      getFreshToken={getFreshToken}
      refresh={refresh}
      getLocale={() => locale}
      onUnauthorized={(reason) => {
        const { pathname, search, hash } = window.location;
        if (session.read()) {
          // The provider's end listener takes the person to the sign-in page with the reason (`/login?reason=revoked&from=…`).
          session.end(endReasonOf(reason), { revoke: false });
          return;
        }
        if (pathname === LOGIN_PATH || pathname === "/mfa" || pathname === "/password") return;
        // Hard navigation on purpose: it also resets in-memory state; the login page returns the user to `from` afterwards.
        window.location.assign(signInUrl(LOGIN_PATH, null, `${pathname}${search}${hash}`));
      }}
    >
      {children}
    </ApiProvider>
  );
}

function HandoffPending() {
  return (
    <div className="grid min-h-screen place-items-center bg-sand-50">
      <Loader2 className="size-6 animate-spin text-aegold-700" aria-hidden />
    </div>
  );
}

function LocaleDirection({ children }: { children: ReactNode }) {
  const { dir } = useLocale();
  return <UiProvider dir={dir}>{children}</UiProvider>;
}

export function Providers({ children }: { children: ReactNode }) {
  return (
    <AuthProvider storageKey={AUTH_STORAGE_KEY} apiBase={API_BASE} channelName={ECONIC_SESSION_CHANNEL} loginPath={LOGIN_PATH}>
      <ApiBridge>
        <HandoffGate fallback={<HandoffPending />}>
          <SsoCallbackGate fallback={<HandoffPending />}>
            <LocaleDirection>
              <SessionManager>{children}</SessionManager>
            </LocaleDirection>
          </SsoCallbackGate>
        </HandoffGate>
      </ApiBridge>
    </AuthProvider>
  );
}
